/**
 * TripBookingModal — WeRoad-style multi-step booking flow.
 *
 * Step 1: Travellers (count, optional gender breakdown)
 * Step 2: Room option (cards with per-card price + capacity)
 * Step 3: Review + pay (opens Stripe Checkout)
 *
 * Falls back gracefully when the trip has no room_options or
 * collect_gender is disabled — auto-skips those steps.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Minus, Plus, Bed, Loader2, Check, ArrowLeft, AlertCircle, User, UserRound } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { authHeaders } from "@/lib/firebase";

const currencySym = (c) => ({ EUR: "€", GBP: "£", USD: "$" }[(c || "USD").toUpperCase()] || "$");

const Stepper = ({ label, value, onChange, max, icon: Icon, testidSuffix }) => (
  <div className="flex items-center justify-between py-3 border-b border-border last:border-0" data-testid={`stepper-${testidSuffix}`}>
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
      <span className="text-sm font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={value <= 0}
        className="w-9 h-9 rounded-full border border-border flex items-center justify-center disabled:opacity-30 hover:bg-muted transition-colors"
        aria-label="Decrease"
        data-testid={`stepper-${testidSuffix}-minus`}
      >
        <Minus className="w-4 h-4" />
      </button>
      <span className="font-display text-xl w-6 text-center tabular-nums" data-testid={`stepper-${testidSuffix}-value`}>{value}</span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        disabled={typeof max === "number" && value >= max}
        className="w-9 h-9 rounded-full border-2 border-primary text-primary flex items-center justify-center disabled:opacity-30 hover:bg-primary/10 transition-colors"
        aria-label="Increase"
        data-testid={`stepper-${testidSuffix}-plus`}
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  </div>
);

export const TripBookingModal = ({ open, onOpenChange, trip }) => {
  // ---- Step management ----
  const [step, setStep] = useState(1);
  useEffect(() => { if (open) setStep(1); }, [open]);

  // ---- Step-1 state: travellers ----
  const collectGender = !!trip?.collect_gender;
  const [female, setFemale] = useState(0);
  const [male, setMale] = useState(0);
  const [other, setOther] = useState(0);
  const [count, setCount] = useState(1);
  const guestCount = collectGender ? female + male + other : count;

  // ---- Step-2 state: room option ----
  const [roomId, setRoomId] = useState("");
  const [mixedOk, setMixedOk] = useState(false);
  const rooms = trip?.room_options || [];
  const tripSpotsLeft = trip?.spots_left;

  // Compute eligible rooms based on gender + capacity + group size
  const roomEligibility = useMemo(() => {
    return rooms.map((r) => {
      const cap = parseInt(r.capacity || 0, 10);
      const left = r.spots_left;
      const spotsLeft = typeof left === "number" ? left : (cap > 0 ? cap : null);
      const restriction = (r.gender_restriction || "any").toLowerCase();
      let reason = null;
      if (spotsLeft !== null && spotsLeft < guestCount) {
        reason = `Only ${spotsLeft} spot${spotsLeft !== 1 ? "s" : ""} left`;
      } else if (restriction === "female") {
        if (collectGender) {
          if (male > 0 || other > 0) reason = "Female-only room — male/other travellers can't pick this";
        }
      } else if (restriction === "male") {
        if (collectGender) {
          if (female > 0 || other > 0) reason = "Male-only room — female/other travellers can't pick this";
        }
      }
      return { ...r, spotsLeft, eligible: reason === null, reason };
    });
  }, [rooms, guestCount, female, male, other, collectGender]);

  // ---- Submit ----
  const [submitting, setSubmitting] = useState(false);
  const selectedRoom = roomEligibility.find(r => r.id === roomId);
  const basePrice = parseFloat(trip?.price || 0);
  const roomModifier = parseFloat(selectedRoom?.price_modifier || 0);
  const perPerson = basePrice + roomModifier;
  const total = perPerson * guestCount;

  // Validation per step
  const canAdvanceFromStep1 = guestCount > 0 && (
    typeof tripSpotsLeft !== "number" || tripSpotsLeft >= guestCount
  );
  const hasRoomOptions = rooms.length > 0;
  const canAdvanceFromStep2 = !hasRoomOptions || !!roomId;

  const handlePay = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const guests = [];
      for (let i = 0; i < female; i++) guests.push({ gender: "female" });
      for (let i = 0; i < male; i++) guests.push({ gender: "male" });
      for (let i = 0; i < other; i++) guests.push({ gender: "other" });
      if (!collectGender) {
        for (let i = 0; i < count; i++) guests.push({ gender: "unspecified" });
      }
      const payload = {
        origin_url: window.location.origin,
        room_option_id: roomId || null,
        guests,
        mixed_room_ok: !!mixedOk,
        guest_count: guestCount,
      };
      const res = await fetch(`${API}/trips/${trip.trip_id}/book/checkout`, {
        method: "POST", headers, body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        toast.error("Failed to create payment session");
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Booking failed");
      }
    } catch (e) {
      toast.error("Network error — try again");
    } finally {
      setSubmitting(false);
    }
  };

  const totalSteps = hasRoomOptions ? 3 : 2;
  const currency = trip?.currency || "USD";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="trip-booking-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Book your spot</DialogTitle>
          <DialogDescription>
            Step {step} of {totalSteps} · {trip?.title}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1 — Travellers */}
        {step === 1 && (
          <div className="space-y-1" data-testid="booking-step-travellers">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">For how many people are you booking?</p>
            {collectGender ? (
              <>
                <Stepper label="Female" value={female} onChange={setFemale} icon={UserRound} testidSuffix="female" />
                <Stepper label="Male" value={male} onChange={setMale} icon={User} testidSuffix="male" />
                <Stepper label="Other" value={other} onChange={setOther} icon={User} testidSuffix="other" />
              </>
            ) : (
              <Stepper label="Travellers" value={count} onChange={setCount} icon={User} testidSuffix="count" />
            )}
            {typeof tripSpotsLeft === "number" && (
              <p className="text-xs text-muted-foreground pt-2">
                {tripSpotsLeft} spot{tripSpotsLeft !== 1 ? "s" : ""} left on this trip
              </p>
            )}
            {!canAdvanceFromStep1 && guestCount > 0 && (
              <p className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 mt-2">
                <AlertCircle className="w-3.5 h-3.5" /> Not enough spots left for your group size
              </p>
            )}
          </div>
        )}

        {/* Step 2 — Room option (only if rooms exist) */}
        {step === 2 && hasRoomOptions && (
          <div className="space-y-2" data-testid="booking-step-room">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Pick your room</p>
            {roomEligibility.map((r) => {
              const total = (basePrice + (parseFloat(r.price_modifier) || 0)) * guestCount;
              const selected = roomId === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  disabled={!r.eligible}
                  onClick={() => setRoomId(r.id)}
                  data-testid={`booking-room-${r.id}`}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    selected
                      ? "border-primary bg-primary/5 ring-2 ring-primary/40"
                      : r.eligible
                        ? "border-border hover:border-primary/50 bg-card"
                        : "border-dashed border-border bg-muted/30 opacity-60 cursor-not-allowed"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Bed className="w-4 h-4 text-primary shrink-0" />
                        <span className="font-medium">{r.label}</span>
                        {selected && <Check className="w-4 h-4 text-primary" />}
                      </div>
                      {r.description && <p className="text-xs text-muted-foreground leading-snug">{r.description}</p>}
                      {r.spotsLeft !== null && r.spotsLeft <= 4 && r.spotsLeft > 0 && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                          Only {r.spotsLeft} spot{r.spotsLeft !== 1 ? "s" : ""} left
                        </p>
                      )}
                      {!r.eligible && r.reason && (
                        <p className="text-[11px] text-red-600 dark:text-red-400 mt-1">{r.reason}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-display text-lg leading-none">{currencySym(currency)}{total.toFixed(0)}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {guestCount > 1 ? `for ${guestCount} ppl` : "total"}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}

            {trip?.allow_mixed_rooms && (
              <label className="flex items-start gap-2 cursor-pointer mt-3 p-3 rounded-xl bg-muted/40">
                <Checkbox
                  checked={mixedOk}
                  onCheckedChange={(v) => setMixedOk(!!v)}
                  data-testid="booking-mixed-room-ok"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium leading-tight">I&apos;m OK with a mixed room</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    You may share with travellers of a different gender. Everyone has their own bed and it&apos;s always travellers from your group — no strangers.
                  </p>
                </div>
              </label>
            )}
          </div>
        )}

        {/* Step 3 — Review */}
        {(step === 3 || (step === 2 && !hasRoomOptions)) && (
          <div className="space-y-3" data-testid="booking-step-review">
            <div className="rounded-xl bg-muted/40 p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Trip</span>
                <span className="font-medium text-right">{trip?.title}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Travellers</span>
                <span className="font-medium">
                  {guestCount}
                  {collectGender && (female + male + other > 0) && (
                    <span className="text-muted-foreground ml-1">
                      ({female ? `${female}F` : ""}{female && male ? "/" : ""}{male ? `${male}M` : ""}{(female || male) && other ? "/" : ""}{other ? `${other}O` : ""})
                    </span>
                  )}
                </span>
              </div>
              {selectedRoom && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Room</span>
                  <span className="font-medium">{selectedRoom.label}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Per person</span>
                <span>{currencySym(currency)}{perPerson.toFixed(0)}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between font-display text-lg">
                <span>Total</span>
                <span>{currencySym(currency)}{total.toFixed(0)}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              You&apos;ll be redirected to Stripe to complete payment. Refund policy: full refund &gt;60 days, 50% 30-60 days, no refund within 30 days.
            </p>
          </div>
        )}

        {/* Footer — back / next / pay */}
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-border mt-2">
          {step > 1 ? (
            <Button variant="ghost" size="sm" onClick={() => setStep(step - 1)} className="gap-1" data-testid="booking-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
          ) : <div />}

          {step < totalSteps ? (
            <Button
              onClick={() => {
                if (step === 1 && !canAdvanceFromStep1) return;
                if (step === 2 && !canAdvanceFromStep2) return;
                setStep(step + 1);
              }}
              disabled={(step === 1 && !canAdvanceFromStep1) || (step === 2 && !canAdvanceFromStep2)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="booking-next-btn"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handlePay}
              disabled={submitting || guestCount <= 0}
              className="bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5"
              data-testid="booking-pay-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Pay {currencySym(currency)}{total.toFixed(0)}</>}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TripBookingModal;
